import { EditUser, ErrorMsg } from './EditAdminUserModal';
import { NewUser } from '../create-user/CreateUser';
import { CourierTravelMode, RoleType } from '../../types/userTypes';
import { ReqEnum } from '../../const/requestStatus';
import RegEx from '../new-order/regEx';
import {
  getMessageForWrongOrgNumber,
  isOrganisationNumberValid,
} from '../../utils/country';

declare interface FieldValidationMsg {
  message: string;
}

function firstNameOk(firstName: string): FieldValidationMsg | undefined {
  if (firstName.length < 1) {
    return { message: 'Please enter the first name' };
  }
}

function phoneCodeOk(phoneCode: string): FieldValidationMsg | undefined {
  if (phoneCode.length < 1) {
    return { message: 'Please enter the phone country code' };
  }
}

function phoneCodeOkInvalidMsg(
  phoneCode: string
): FieldValidationMsg | undefined {
  if (phoneCode.length > 1 && !phoneCode.match(RegEx.countryCode)) {
    return { message: 'Invalid phone country code' };
  }
}

function phoneNrOkInvalidMsg(
  phoneNumber: string
): FieldValidationMsg | undefined {
  if (phoneNumber.length < 1) {
    return { message: 'Please enter the phone number' };
  }
}

function phoneNrOkLengthInvalidMsg(
  phoneNumber: string
): FieldValidationMsg | undefined {
  if (phoneNumber.length > 1 && !phoneNumber.match(RegEx.phone)) {
    return { message: 'Invalid phone number' };
  }
}

function emailInvalidMsg(
  newEmail: string,
  validateNewUser: boolean
): FieldValidationMsg | undefined {
  if (newEmail && newEmail.trim().length > 1 && !newEmail.match(RegEx.email)) {
    return { message: 'Invalid email address' };
  }
  if (validateNewUser && !newEmail) {
    return {
      message: 'Please enter the email',
    };
  }
}

function companyNumberInvalidMsg(
  companyNumber: string,
  country: string
): FieldValidationMsg | undefined {
  if (
    companyNumber !== '' &&
    !isOrganisationNumberValid(companyNumber, country)
  ) {
    const companyNumber = {
      message: getMessageForWrongOrgNumber(country),
    };
    return companyNumber;
  }
}

function companyNumberWithEnhetsRegisterCheckInvalidMsg(
  companyNumber: string,
  reqCompanyNameFromFetch: ReqEnum
): FieldValidationMsg | undefined {
  if (reqCompanyNameFromFetch !== ReqEnum.SUCCESS) {
    const companyName = {
      message: 'This organisation number does not exist',
    };
    return companyName;
  }
}

function companyNumberForSenderInvalidMsg(
  user: NewUser
): FieldValidationMsg | undefined {
  if (user.companyNumber === undefined || user.country === undefined) {
    return {
      message: 'companyNumber or country is undefined',
    };
  }

  if (
    (!user.accountId || user.accountId === -1) &&
    user.companyNumber !== '' &&
    !isOrganisationNumberValid(user.companyNumber, user.country)
  ) {
    const companyNumber = {
      message: getMessageForWrongOrgNumber(user.country),
    };
    return companyNumber;
  }
}

function paymentByInvoiceAllowedInvalidMsg(
  user: EditUser
): FieldValidationMsg | undefined {
  if (!user.companyName || !user.companyNumber) {
    const paymentByInvoiceAllowed = {
      message: 'Please enter a company name and organization number',
    };
    return paymentByInvoiceAllowed;
  }
}

function paymentByInvoiceAllowedForSenderInvalidMsg(
  user: NewUser
): FieldValidationMsg | undefined {
  if (
    !user.accountId &&
    user.paymentByInvoiceAllowed &&
    user.paymentByInvoiceAllowed &&
    (!user.companyName || !user.companyNumber)
  ) {
    const paymentByInvoiceAllowed = {
      message: 'Please enter a company name and organization number',
    };
    return paymentByInvoiceAllowed;
  }
}

function selectAccountInvalidMsg(
  userAccountId?: number | null
): FieldValidationMsg | undefined {
  if (userAccountId === null) {
    return {
      message: 'Select an existing account or create a new one',
    };
  }
}

function selectAccountIdToMatchCompanyNameInvalidMsg(
  user: NewUser,
  CreateNewAccountId: number
): FieldValidationMsg | undefined {
  if (user.accountId === CreateNewAccountId && !user.companyName) {
    return {
      message:
        user.country === 'Norway'
          ? 'Please enter a correct company number'
          : 'Please fill in a valid company name',
    };
  }
}

function selectAccountIdToMatchCompanyNumberInvalidMsg(
  user: NewUser,
  CreateNewAccountId: number
): FieldValidationMsg | undefined {
  if (user.accountId === CreateNewAccountId && !user.companyNumber) {
    return {
      message: 'Please enter an organization number',
    };
  }
}

function etaOffsetSecondsInvalidMsg(
  etaOffsetSeconds: number
): FieldValidationMsg | undefined {
  if (Math.abs(etaOffsetSeconds) > 60 * 15) {
    return {
      message: 'The ETA offset is too big',
    };
  }
}

function preferredGeoZonesInvalidMsg(
  preferredGeoZones: string[]
): FieldValidationMsg | undefined {
  if (preferredGeoZones === undefined) {
    return {
      message: 'no courier area set',
    };
  }
}

type FieldErrors = Record<string, ErrorMsg | undefined>;

export function validateCourierFields(
  user: EditUser,
  reqCompanyNameFromFetch: ReqEnum
): FieldErrors {
  const fieldErrors: FieldErrors = {};

  fieldErrors.firstName = firstNameOk(user.firstName);

  fieldErrors.phoneCode = phoneCodeOk(user.phoneCode);

  fieldErrors.phoneCode = phoneCodeOkInvalidMsg(user.phoneCode);

  fieldErrors.phoneNumber = phoneNrOkInvalidMsg(user.phoneNumber);

  fieldErrors.phoneNumber = phoneNrOkLengthInvalidMsg(user.phoneNumber);

  fieldErrors.newEmail = emailInvalidMsg(user.email, false);

  if (!(user.isSorter || user.isCourier)) {
    fieldErrors.tasks = {
      message: 'Please select at least one task.',
    };
  }

  fieldErrors.companyName =
    user.companyNumber !== undefined
      ? companyNumberWithEnhetsRegisterCheckInvalidMsg(
          user.companyNumber,
          reqCompanyNameFromFetch
        )
      : undefined;

  fieldErrors.companyNumber =
    (user.companyNumber &&
      user.country &&
      companyNumberInvalidMsg(user.companyNumber, user.country)) ||
    undefined;

  fieldErrors.paymentByInvoiceAllowed = paymentByInvoiceAllowedInvalidMsg(user);

  fieldErrors.etaOffsetSeconds = etaOffsetSecondsInvalidMsg(
    user.etaOffsetSeconds
  );

  fieldErrors.preferredGeoZones =
    user.preferredGeoZones &&
    preferredGeoZonesInvalidMsg(user.preferredGeoZones);

  return fieldErrors;
}

export function IsThereAnyErrors(
  fieldErrors: FieldErrors,
  ignoredFields?: string[]
): boolean {
  const errorsAsArray: [string, ErrorMsg | undefined][] =
    Object.entries(fieldErrors);
  const isThereAnyErrors =
    errorsAsArray
      .filter(obj => !ignoredFields || !ignoredFields.includes(obj[0]))
      .filter(obj => obj[1] !== undefined).length > 0;

  return isThereAnyErrors;
}

export function validateFields(user: EditUser): FieldErrors {
  const fieldErrors: FieldErrors = {};

  fieldErrors.firstName = firstNameOk(user.firstName);
  fieldErrors.phoneCode = phoneCodeOkInvalidMsg(user.phoneCode);
  fieldErrors.phoneNumber = phoneNrOkLengthInvalidMsg(user.phoneNumber);
  fieldErrors.newEmail = emailInvalidMsg(user.newEmail, false);

  if (user.role === RoleType.SENDER) {
    fieldErrors.companyNumber =
      (user.companyNumber &&
        user.country &&
        companyNumberInvalidMsg(user.companyNumber, user.country)) ||
      undefined;
    fieldErrors.paymentByInvoiceAllowed =
      paymentByInvoiceAllowedInvalidMsg(user);
  }

  return fieldErrors;
}

export function validateNewUser(
  user: NewUser,
  CreateNewAccountId: number
): FieldErrors {
  const fieldErrors: FieldErrors = {};
  const { role, regions } = user;

  fieldErrors.email = emailInvalidMsg(user.email, true);
  fieldErrors.firstName = firstNameOk(user.firstName);
  fieldErrors.phoneCode =
    phoneCodeOk(user.phoneCode) || phoneCodeOkInvalidMsg(user.phoneCode);
  fieldErrors.phoneNumber =
    phoneNrOkInvalidMsg(user.phoneNumber) ||
    phoneNrOkLengthInvalidMsg(user.phoneNumber);

  if (role === RoleType.SENDER || role === RoleType.COURIER) {
    fieldErrors.companyNumber = companyNumberForSenderInvalidMsg(user);

    fieldErrors.paymentByInvoiceAllowed =
      paymentByInvoiceAllowedForSenderInvalidMsg(user);

    fieldErrors.account = selectAccountInvalidMsg(user.accountId);
    fieldErrors.companyName = selectAccountIdToMatchCompanyNameInvalidMsg(
      user,
      CreateNewAccountId
    );

    // does the field company number has already some errors?
    if (!fieldErrors.companyNumber) {
      fieldErrors.companyNumber = selectAccountIdToMatchCompanyNumberInvalidMsg(
        user,
        CreateNewAccountId
      );
    }
  }

  if (role === RoleType.COURIER) {
    if (user.travelMode === CourierTravelMode.None) {
      fieldErrors.travelMode = { message: 'Please select a travel mode' };
    }

    if (!(user.isSorter || user.isCourier)) {
      fieldErrors.tasks = { message: 'Please select at least one task.' };
    }
  }

  if (
    (role === RoleType.ADMIN || role === RoleType.COURIER) &&
    regions.length === 0
  ) {
    fieldErrors.regions = { message: 'Please select one or more regions' };
  }

  return fieldErrors;
}
